import { useMutation, useQueryClient } from 'react-query'
import { useRouter } from 'next/router'
import { format, addHours } from 'date-fns'
import { useCommentsKey } from 'querys/comments'
import NProgress from 'nprogress'
import {
  createCommentService,
  deleteCommentService,
  getRepliesService,
  reactToCommentService,
} from 'services/comment'
import useSession from 'querys/session'

export function useCreateComment() {
  const router = useRouter()
  const { query: { course: course_slug, lesson_slug } } = router
  const { token, profile: user } = useSession()
  const COMMENTS_KEY = useCommentsKey()
  const queryClient = useQueryClient()
  return useMutation(
    ({ comment, parent }) => createCommentService({
      course_slug,
      lesson_slug,
      token,
      comment,
      parent,
    }),
    {
      onMutate: async ({ comment: content, parent, participants }) => {
        NProgress.start()
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries(COMMENTS_KEY)

        // Snapshot the previous value
        const previousComments = queryClient.getQueryData(COMMENTS_KEY)

        const newComment = {
          uuid: Math.floor(Math.random() * 10000),
          author: {
            photo: user.image,
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
          },
          created_at: '',
          content,
          likes: 0,
          wands: 0,
          nogiveups: 0,
          has_replies: false,
          reactions: {
            like: false,
            wand: false,
            nogiveup: false,
          },
          replies_count: 0,
          replies: [],
          participants,
        }

        // Optimistically update to the new value
        if (!parent) {
          queryClient.setQueryData(COMMENTS_KEY, (commentsData) => {
            commentsData.results.unshift(newComment)
            commentsData.count += 1
            return commentsData
          })
        } else {
          queryClient.setQueryData(['GET_COMMENT_REPLIES', parent], (replies) => {
            if (replies) return [...replies, newComment]
            return [newComment]
          })
          queryClient.setQueryData(COMMENTS_KEY, (commentsData) => {
            commentsData.results.find((comment) => {
              if (comment.uuid === parent) {
                comment.has_replies = true
                comment.replies_count = comment.replies_count + 1
                comment.replies.unshift(newComment)
                return comment
              }
            })
            return commentsData
          })
        }

        // Return a context object with the snapshotted value
        return { previousComments, parent }
      },
      onSuccess: () => {
        NProgress.done()
      },
      // If the mutation fails, use the context returned from onMutate to roll back
      onError: (err, newTodo, context) => {
        queryClient.setQueryData(COMMENTS_KEY, context.previousTodos)
      },
      onSettled: (data, error, variables, { parent }) => {
        if (parent) {
          queryClient.invalidateQueries(['GET_COMMENT_REPLIES', parent])
          return
        }
        queryClient.invalidateQueries(COMMENTS_KEY)
      },
    },
  )
}

export function useDeleteComment() {
  const { token } = useSession()
  return useMutation(
    (commentId) => deleteCommentService({
      token,
      commentId,
    }),
  )
}

export function useRepliesToComment() {
  const { course, lesson_slug } = useRouter().query
  const { token } = useSession()
  const COMMENTS_KEY = useCommentsKey()
  const queryClient = useQueryClient()
  return useMutation(
    ({ parent }) => getRepliesService({
      course,
      lesson_slug,
      parent,
      token,
    }),
    {
      onSuccess: (replies, { parent }) => {
        queryClient.setQueryData(COMMENTS_KEY, (commentsData) => {
          commentsData.results.forEach((comment) => {
            if (comment.uuid === parent) {
              comment.replies = comment.replies.concat(replies)
            }
          })
          return commentsData
        })
      },
    },
  )
}

export function useReactToComment() {
  const { query: { course, lesson_slug } } = useRouter()
  const { token } = useSession()
  return useMutation(
    ({ payload }) => reactToCommentService({
      course,
      lesson_slug,
      token,
      payload,
    }),
  )
}
